import { z } from 'zod';

/**
 * Edukacijski sadržaj — admin strana (`/v1/admin/content`).
 *
 * Čitanje za klijenta živi u `phase3-types.ts` (`ContentListItem`, `ContentDetail`)
 * i namjerno je uži oblik: klijent nikad ne vidi skice ni tijelo zaključanog
 * članka. Ovdje je puni redak, jer admin uređuje oba jezika odjednom.
 */

export const contentCategories = [
  'nutrition',
  'training',
  'recovery',
  'mobility',
  'lifestyle',
] as const;

export const contentCategorySchema = z.enum(contentCategories);

/**
 * Slug završi u URL-u i u `hreflang` paru, pa nema dijakritike, razmaka ni
 * kosih crta — samo mala slova, brojke i crtica između njih.
 */
export const contentSlugSchema = z
  .string()
  .min(2)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Dopuštena su mala slova, brojke i crtica');

export const contentArticleInput = z.object({
  category: contentCategorySchema,
  slugHr: contentSlugSchema,
  /** Bez engleskog sluga članak postoji samo na hrvatskom — to je dopušteno. */
  slugEn: contentSlugSchema.nullable().optional(),
  titleHr: z.string().min(2).max(200),
  titleEn: z.string().min(2).max(200).nullable().optional(),
  /** Markdown. Prikaz ga renderira; ovdje se sprema kakav jest. */
  bodyHr: z.string().max(50_000).nullable().optional(),
  bodyEn: z.string().max(50_000).nullable().optional(),
  coverKey: z.string().max(300).nullable().optional(),
  fileKey: z.string().max(300).nullable().optional(),
  /** null = javno; inače traži aktivni entitlement na tom proizvodu. */
  requiresProductId: z.string().uuid().nullable().optional(),
  published: z.boolean().default(false),
});

/** Izmjena šalje samo promijenjena polja; slug se i tad provjerava na jedinstvenost. */
export const contentArticlePatch = contentArticleInput.partial();

export const adminContentArticleOutput = z.object({
  id: z.string(),
  category: contentCategorySchema,
  slugHr: z.string(),
  slugEn: z.string().nullable(),
  titleHr: z.string(),
  titleEn: z.string().nullable(),
  bodyHr: z.string().nullable(),
  bodyEn: z.string().nullable(),
  coverKey: z.string().nullable(),
  fileKey: z.string().nullable(),
  requiresProductId: z.string().nullable(),
  published: z.boolean(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ContentArticleInput = z.input<typeof contentArticleInput>;
export type ContentArticlePatch = z.input<typeof contentArticlePatch>;
export type AdminContentArticle = z.infer<typeof adminContentArticleOutput>;

/* ──────────────────────────────────────────────────────────────────────────
   Transformacije („prije / poslije")

   Javni sajt ih čita kroz `/v1/public/testimonials`, a unosi ih trener ili
   admin. Fotografije se ne šalju kroz ovaj zapis nego kroz potpisani prijenos
   (`POST .../photo-url`), pa ovdje stoji samo KLJUČ objekta: potpisana adresa
   vrijedi sat vremena i spremljena bi „nestala" tjedan dana kasnije.
   ────────────────────────────────────────────────────────────────────────── */

/** Mjerljiv rezultat. Bez njega kartica na sajtu nema što tvrditi. */
export const transformationMetric = z.object({
  unit: z.enum(['kg', 'cm']),
  /** Negativno je gubitak, pozitivno dobitak. Nula nije rezultat. */
  value: z.number().int().min(-200).max(200).refine((n) => n !== 0, 'Nula nije rezultat.'),
});

export const transformationInput = z.object({
  authorName: z.string().trim().min(2).max(80),
  authorAge: z.number().int().min(14).max(100).nullable().optional(),
  textHr: z.string().trim().max(600).nullable().optional(),
  textEn: z.string().trim().max(600).nullable().optional(),
  photoBeforeKey: z.string().min(1).max(300).nullable().optional(),
  photoAfterKey: z.string().min(1).max(300).nullable().optional(),
  metric: transformationMetric.nullable().optional(),
  published: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

export const transformationPatch = transformationInput.partial();

/** Prijenos fotografije: klijent traži adresu, pa sam šalje bajtove. */
export const transformationPhotoUrlInput = z.object({
  faza: z.enum(['prije', 'poslije']),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
});

export const adminTransformationOutput = z.object({
  id: z.string(),
  authorName: z.string(),
  authorAge: z.number().int().nullable(),
  textHr: z.string().nullable(),
  textEn: z.string().nullable(),
  photoBeforeKey: z.string().nullable(),
  photoAfterKey: z.string().nullable(),
  /** Adresa za prikaz u portalu; ključ je ono što se sprema. */
  photoBeforeUrl: z.string().nullable(),
  photoAfterUrl: z.string().nullable(),
  metric: transformationMetric.nullable(),
  published: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TransformationMetric = z.infer<typeof transformationMetric>;
export type TransformationInput = z.input<typeof transformationInput>;
export type TransformationPatch = z.input<typeof transformationPatch>;
export type TransformationPhotoUrlInput = z.input<typeof transformationPhotoUrlInput>;
export type AdminTransformation = z.infer<typeof adminTransformationOutput>;
